import { AuthService } from './../../service/auth.service';
import {
	AfterViewInit,
	ChangeDetectionStrategy,
	Component,
	ElementRef,
	OnDestroy,
	OnInit,
	ViewChild,
} from '@angular/core';
import { AgentDto } from '../../service/dto';
import { environment } from '../../../environments/environment';

export type LoginModeEnum = 'Login' | 'ForgotPassword' | 'Blocked' | 'Suspended' | 'TwoFactorAuth';

export class Callback {
	agent?: AgentDto;
	mode!: LoginModeEnum;
	credentials?: { username: string, password: string };
}

@Component({
	selector: 'app-login',
	templateUrl: './login.component.html',
	styleUrls: ['./login.component.scss'],
	standalone: false,
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoginComponent implements AfterViewInit, OnInit, OnDestroy {
	@ViewChild('neuralCanvas') neuralCanvas!: ElementRef<HTMLCanvasElement>;
	counter = 0;
	mode: LoginModeEnum = 'Login';
	agent?: AgentDto;
	credentials?: { username: string, password: string };

	private animationFrameId?: number;
	private particles: any[] = [];
	private width!: number;
	private height!: number;
	private lastTime = 0;
	private fps = 15;
	private resizeHandler = () => this.handleResize();

	loginLogoLeftUrl!: string;
	loginLogoRightUrl!: string;
	loginImageLeftUrl!: string;
	loginImageRightUrl!: string;

	constructor(private authService: AuthService) { }

	ngOnInit(): void {
		this.loginLogoLeftUrl = environment.loginLogoLeftUrl;
		this.loginLogoRightUrl = environment.loginLogoRightUrl;
		this.loginImageLeftUrl = environment.loginImageLeftUrl;
		this.loginImageRightUrl = environment.loginImageRightUrl;
	}

	ngAfterViewInit() {
		this.initNeuralBackground();
	}

	ngOnDestroy() {
		if (this.animationFrameId) cancelAnimationFrame(this.animationFrameId);
		window.removeEventListener('resize', this.resizeHandler);
	}

	doUpdateState(input: Callback) {
		this.mode = input.mode;
		this.agent = input.agent;
		this.credentials = input.credentials;
	}

	private initNeuralBackground() {
		const canvas = this.neuralCanvas.nativeElement;
		const ctx = canvas.getContext('2d');
		if (!ctx) return;

		window.addEventListener('resize', this.resizeHandler);
		this.handleResize();
		this.draw(ctx);
	}

	private handleResize() {
		const canvas = this.neuralCanvas.nativeElement;
		this.width = canvas.width = window.innerWidth;
		this.height = canvas.height = window.innerHeight;
		this.createParticles();
	}

	private createParticles() {
		const count = Math.floor((this.width * this.height) / 8500);
		this.particles = [];
		for (let i = 0; i < count; i++) {
			this.particles.push({
				x: Math.random() * this.width,
				y: Math.random() * this.height,
				vx: (Math.random() - 0.5) * 0.5,
				vy: (Math.random() - 0.5) * 0.5,
				radius: Math.random() * 1.8 + 0.6,
				color: Math.random() > 0.5
					? 'rgba(7, 1, 53, 0.9)'
					: 'rgba(0,180,255,0.9)',
			});
		}
	}

	private draw(ctx: CanvasRenderingContext2D, time = 0) {
		// const delta = time - this.lastTime;
		// if (delta < 1000 / this.fps) {
		// 	this.animationFrameId = requestAnimationFrame((t) => this.draw(ctx, t));
		// 	return;
		// }
		this.lastTime = time;

		ctx.clearRect(0, 0, this.width, this.height);
		ctx.fillStyle = 'rgba(13, 27, 61, 0.45)';
		ctx.fillRect(0, 0, this.width, this.height);

		for (const p of this.particles) {
			p.x += p.vx;
			p.y += p.vy;
			if (p.x < 0 || p.x > this.width) p.vx *= -1;
			if (p.y < 0 || p.y > this.height) p.vy *= -1;

			ctx.beginPath();
			ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
			ctx.fillStyle = p.color;
			ctx.shadowBlur = 10;
			ctx.shadowColor = p.color;
			ctx.fill();
		}

		const maxDist = 160;
		for (let i = 0; i < this.particles.length; i++) {
			for (let j = i + 1; j < this.particles.length; j++) {
				const dx = this.particles[i].x - this.particles[j].x;
				const dy = this.particles[i].y - this.particles[j].y;
				const dist = Math.sqrt(dx * dx + dy * dy);
				if (dist < maxDist) {
					const alpha = 1 - dist / maxDist;
					const gradient = ctx.createLinearGradient(
						this.particles[i].x,
						this.particles[i].y,
						this.particles[j].x,
						this.particles[j].y
					);
					gradient.addColorStop(0, `rgba(255,165,0,${alpha * 0.5})`);
					gradient.addColorStop(1, `rgba(0,180,255,${alpha * 0.5})`);

					ctx.strokeStyle = gradient;
					ctx.lineWidth = 0.6;
					ctx.beginPath();
					ctx.moveTo(this.particles[i].x, this.particles[i].y);
					ctx.lineTo(this.particles[j].x, this.particles[j].y);
					ctx.stroke();
				}
			}
		}
		// this.animationFrameId = requestAnimationFrame((t) => this.draw(ctx, t));
	}
	getCounter() {
		return this.counter++;
	}
}
